import { Fragment, FC, useEffect, useState } from "react";
import { Box, Divider, Tooltip, Typography } from "@mui/material";
import {
  RedoxResultObservationType,
  RedoxResultType,
} from "DataTypes/Services/Patient.type";
import Loader from "Components/Loader";
import NoData from "Components/NoData";
import { SideModalProps } from "DataTypes/Modal";
import { PatientInitialStateType, UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import RedoxFormatter from "Utils/Redox.Formatter";
import DateTime from "Utils/DateTime";
import UpdateResult from "./UpdateResult";
import { MockResultList } from "./Data";
import { CommonStyle } from "Components";
import ImgPath from "Constant/Imgs";
import RedoxApi from "Service/Redox.api";
import CstmBtn from "Components/CstmBtn";
import useEhrSync from "Hooks/useEhrSync";
import EhrLastSync from "Components/EhrLastSync";
import useEhrRefresh from "Hooks/useEhrRefresh";
import useClinicalCounts from "Hooks/useClinicalCounts";
import { EhrDomains } from "Constant/Ehr";
import { label } from "Constant/En";

export interface DataActionType {
  data: RedoxResultType | null;
  action: "ADD" | "EDIT" | "VIEW" | "DELETE" | null;
}

const Result: FC<SideModalProps> = (props) => {
  const { onClose } = props;
  const { patientInfo, ehr }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient,
  );
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const [resultData, setResultData] = useState<RedoxResultType[]>([]);
  const [loading, setLoading] = useState(true);
  const crudPermission = myInfo.roleId != 6 && ehr.vendor === null;
  const [dataAction, setDataAction] = useState<DataActionType>({
    data: null,
    action: null,
  });

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults: () => void = async () => {
    setLoading(true);
    const res = await RedoxApi.ResultSearch({ id1: patientInfo.id });
    if (res?.success) {
      setResultData(RedoxFormatter.ResultList(res?.data?.results));
    } else setResultData([]);
    setLoading(false);
  };

  const ehrSync = useEhrSync(EhrDomains.LAB_RESULTS);
  useEhrRefresh(EhrDomains.LAB_RESULTS, fetchResults);

  const { counts, refreshCounts } = useClinicalCounts();
  // Undefined until the counts load, so no made-up zero is shown.
  const totalCount: number | undefined = counts?.results?.total;
  // An add, edit or delete moves the header count as well as the list.
  const refreshListAndCounts: () => void = () => {
    fetchResults();
    refreshCounts();
  };

  const handleCloseLayer2: () => void = () => {
    setDataAction({ data: null, action: null });
  };

  const displayResultHead: (data: RedoxResultType) => React.ReactElement = (
    data,
  ) => {
    return (
      <Box sx={{ ...sxMedBox }}>
        <Typography variant="subtitle1" sx={{ ...sxMedName }}>
          {data.resultName || "—"}
        </Typography>
        {crudPermission && (
          <Box
            display="flex"
            justifyContent={"flex-end"}
            sx={{ "&>div": { ml: 1 } }}
            width="30%"
          >
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => setDataAction({ data: data, action: "EDIT" })}
            >
              <ImgPath.EditOutlined titleAccess="Edit" />
            </Box>
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => setDataAction({ data: data, action: "DELETE" })}
            >
              <ImgPath.DeleteOutline titleAccess="Delete" />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const displayObservation: (
    data: RedoxResultObservationType,
    showDivider: boolean
  ) => React.ReactElement = (data, showDivider) => {
    return (
      <Box key={data.id} sx={{ ...sxObsContainer }}>
        <Typography variant="subtitle1" sx={{ ...sxObsName }}>
          {data.observationName || "—"}
        </Typography>
        <Typography variant="subtitle1">
          {data.observationValue
            ? [data.observationValue, data.observationUnit]
              .filter(Boolean)
              .join(" ")
            : "—"}
        </Typography>
        <Typography variant="subtitle1">
          Observation Date: {DateTime.ToLocalDate(data.dateTime)}
        </Typography>
        <Typography variant="subtitle1">
          Status: {data.status || "—"}
        </Typography>
        {showDivider && <Divider sx={{ ...sxObsDivider }} />}
      </Box>
    );
  };

  const Layer1Header = (
    <Box sx={{ ...sxHeader, ...CommonStyle.sxRow }}>
      <Typography fontWeight="bold">
        Results{totalCount != null && ` (${totalCount})`}
      </Typography>
      {/* Controls on one line, with the sync stamp beneath them:
          stacking it under the button alone makes that column taller
          and drops the icons out of line with it. */}
      <Box
        sx={{
          ...CommonStyle.sxFlexDirectionColumn,
          alignItems: "flex-end",
        }}
      >
        <Box sx={{ ...CommonStyle.sxRow }}>
          {ehrSync.canSync && (
            <Tooltip arrow placement="top" title={ehrSync.title}>
              <span>
                <CstmBtn
                  label={label.syncFromEhr}
                  btnType="primary"
                  loading={ehrSync.syncing}
                  loadingText="Syncing..."
                  onClick={ehrSync.handleSync}
                  sxProps={{ mr: 1, py: 0.4, minWidth: "auto", px: 1.2 }}
                />
              </span>
            </Tooltip>
          )}
          {crudPermission && (
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm, mr: 1 }}
              onClick={() => setDataAction({ data: null, action: "ADD" })}
            >
              <ImgPath.PlusIcon titleAccess="Add Result" />
            </Box>
          )}
          <Box
            sx={{ ...CommonStyle.sxRoundIconsMainSm }}
            onClick={() => {
              handleCloseLayer2();
              onClose();
            }}
          >
            <ImgPath.CloseIcon titleAccess="Close" />
          </Box>
        </Box>
        <EhrLastSync sync={ehrSync} />
      </Box>
    </Box>
  );

  const Layer1 = (
    <Fragment>
      {Layer1Header}
      <Box sx={{ ...sxMain }}>
        {loading ? (
          <Loader />
        ) : resultData.length ? (
          resultData.map((el, index) => {
            return (
              <Box key={el.id} sx={{ ...sxContainer }}>
                {displayResultHead(el)}
                {el.observations.length ? (
                  el.observations.map((obsEl, obsIndex) =>
                    displayObservation(
                      obsEl,
                      obsIndex != el.observations.length - 1
                    )
                  )
                ) : (
                  <Typography variant="subtitle1">
                    No Observation Found
                  </Typography>
                )}
                {index != resultData.length - 1 && (
                  <Divider sx={{ mt: 1 }} />
                )}
              </Box>
            );
          })
        ) : (
          <NoData />
        )}
      </Box>
    </Fragment>
  );

  return (
    <Fragment>
      {/* The list column keeps a pixel floor so its rows stop collapsing
          into a horizontal scroll on a narrow screen, and the panel beside
          it takes whatever width is left. */}
      <Box
        sx={{
          ...CommonStyle.sxFlexDirectionColumn,
          width: "30%",
          minWidth: 320,
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {Layer1}
      </Box>
      <Box width="1%"></Box>
      {dataAction.action && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <UpdateResult
            dataAction={dataAction}
            onClose={handleCloseLayer2}
            refreshList={refreshListAndCounts}
          />
        </Box>
      )}
    </Fragment>
  );
};

export default Result;

// The header sizes to its own content rather than to a percentage of the
// modal: with the sync stamp on a second line it needs more than the 10% a
// short viewport gives it, and the overflow used to land on the list below.
const sxHeader = {
  justifyContent: "space-between",
  flexShrink: 0,
  py: 1,
  px: 2,
};

const sxMain = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  px: 2,
};

const sxContainer = {
  py: 1,
};

const sxObsContainer = {
  pl: 2,
  py: 0.5,
};

const sxObsDivider = {
  mt: 0.5,
  borderStyle: "dashed",
};

const sxObsName = {
  fontWeight: "bold",
  lineBreak: "anywhere",
};

const sxMedBox = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
};

const sxMedName = {
  fontWeight: "bold",
  width: "80%",
  lineBreak: "anywhere",
};
